<template>
  <div class="space-y-6">
    <!-- Loading -->
    <div v-if="loading" class="text-center py-8">
      <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">加载中...</p>
    </div>

    <template v-else>
      <!-- 活跃订阅列表 -->
      <div class="border dark:border-gray-700 rounded-lg p-4">
        <h4 class="text-base font-semibold text-gray-900 dark:text-gray-100 mb-4">活跃订阅</h4>

        <div v-if="activeSubscriptions.length === 0" class="text-center py-8 text-gray-500 dark:text-gray-400">
          该用户暂无活跃订阅
        </div>

        <div v-else class="space-y-4">
          <div v-for="subscription in activeSubscriptions" :key="subscription.id"
            class="border dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-700">
            <div class="flex justify-between items-start mb-3">
              <div class="flex items-center gap-2">
                <h5 class="text-base font-semibold text-gray-900 dark:text-gray-100">{{ displayLocalizedText(subscription.plan.name) }}</h5>
                <span :class="getSubscriptionStatusClass(subscription.status)" class="text-xs">
                  {{ formatSubscriptionStatus(subscription.status) }}
                </span>
              </div>
              <div class="flex gap-2">
                <button @click="handleEditEndDate(subscription)" :disabled="actionLoading"
                  class="px-3 py-1.5 text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded border border-indigo-200 dark:border-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                  修改到期时间
                </button>
                <button
                  v-if="canRefundSubscription(subscription)"
                  @click="openRefundDialog(subscription)"
                  :disabled="actionLoading"
                  class="px-3 py-1.5 text-sm text-amber-700 dark:text-amber-300 hover:text-amber-800 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded border border-amber-200 dark:border-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  退款 / 作废订单
                </button>
                <button @click="handleCancelSubscription(subscription.id)" :disabled="actionLoading"
                  class="px-3 py-1.5 text-sm text-red-600 dark:text-red-400 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded border border-red-200 dark:border-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                  取消订阅
                </button>
              </div>
            </div>

            <div class="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span class="text-gray-500 dark:text-gray-400">套餐代码：</span>
                <span class="font-medium text-gray-900 dark:text-gray-100">{{ subscription.plan.planCode }}</span>
              </div>
              <div>
                <span class="text-gray-500 dark:text-gray-400">订阅 ID：</span>
                <span class="font-medium text-gray-900 dark:text-gray-100">#{{ subscription.id }}</span>
              </div>
              <div>
                <span class="text-gray-500 dark:text-gray-400">开始时间：</span>
                <span class="font-medium text-gray-900 dark:text-gray-100">{{ formatDate(subscription.startDate) }}</span>
              </div>
              <div>
                <span class="text-gray-500 dark:text-gray-400">到期时间：</span>
                <span class="font-medium text-gray-900 dark:text-gray-100">{{ formatDate(subscription.endDate) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 创建新订阅 -->
      <div class="border dark:border-gray-700 rounded-lg p-4">
        <h4 class="text-base font-semibold text-gray-900 dark:text-gray-100 mb-4">创建新订阅</h4>
        <div class="space-y-4">
          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">选择套餐和价格方案</label>
            <select v-model="selectedPricingId"
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-200">
              <option value="">请选择套餐和价格方案</option>
              <template v-for="plan in plans" :key="plan.id">
                <option v-for="pricing in plan.pricings" :key="pricing.id" :value="pricing.id">
                  {{ displayLocalizedText(plan.name) }} - ¥{{ (pricing.price / 100).toFixed(2) }} / {{ pricing.durationDays }}天
                </option>
              </template>
            </select>
          </div>
          <div class="flex justify-end">
            <button @click="handleCreateSubscription" :disabled="!selectedPricingId || actionLoading"
              class="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50">
              创建订阅
            </button>
          </div>
        </div>
      </div>
    </template>

    <!-- 订阅历史 -->
    <div class="border dark:border-gray-700 rounded-lg p-4">
      <h4 class="text-base font-semibold text-gray-900 dark:text-gray-100 mb-4">订阅历史</h4>
      <div v-if="subscriptionHistory.length === 0" class="text-center py-4 text-gray-500 dark:text-gray-400">
        暂无订阅历史
      </div>
      <div v-else class="space-y-3">
        <div v-for="sub in subscriptionHistory" :key="sub.id" class="border dark:border-gray-700 rounded p-3 hover:bg-gray-50 dark:hover:bg-gray-700">
          <div class="flex justify-between items-start mb-2">
            <div class="flex-1">
              <div class="flex items-center gap-2">
                <span class="font-medium text-gray-900 dark:text-gray-100">{{ displayLocalizedText(sub.plan.name) }}</span>
                <span :class="getSubscriptionStatusClass(sub.status)" class="text-xs">
                  {{ formatSubscriptionStatus(sub.status) }}
                </span>
              </div>
              <div class="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {{ formatDate(sub.startDate) }} - {{ formatDate(sub.endDate) }}
              </div>
            </div>

            <!-- 手动激活按钮（仅PENDING状态显示） -->
            <div v-if="sub.status === 'PENDING'" class="flex gap-2">
              <button
                @click="handleActivateSubscription(sub.id)"
                :disabled="actionLoading"
                class="px-3 py-1.5 text-sm text-green-600 dark:text-green-400 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-900/20 rounded border border-green-200 dark:border-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="手动激活订阅（用于支付回调失败等特殊情况）">
                手动激活
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 修改到期时间对话框 -->
    <div v-if="showEditEndDateDialog" class="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50"
      @click.self="closeEditEndDateDialog">
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
        <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">修改订阅到期时间</h3>

        <div class="space-y-4">
          <!-- 订阅信息 -->
          <div class="bg-gray-50 dark:bg-gray-700 rounded p-3 text-sm">
            <div class="flex justify-between mb-2">
              <span class="text-gray-500 dark:text-gray-400">套餐名称：</span>
              <span class="font-medium text-gray-900 dark:text-gray-100">{{ displayLocalizedText(editingSubscription?.plan.name) }}</span>
            </div>
            <div class="flex justify-between mb-2">
              <span class="text-gray-500 dark:text-gray-400">当前到期时间：</span>
              <span class="font-medium text-gray-900 dark:text-gray-100">{{ formatDate(editingSubscription?.endDate) }}</span>
            </div>
          </div>

          <!-- 新的到期时间 -->
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              新的到期时间
            </label>
            <input type="datetime-local" v-model="newEndDate"
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-200"
              :min="minEndDate" />

            <!-- 快捷设置按钮 -->
            <div class="mt-3">
              <p class="text-xs text-gray-500 dark:text-gray-400 mb-2">快捷设置：</p>
              <div class="flex gap-2">
                <button type="button" @click="resetToOriginal" :disabled="actionLoading"
                  class="px-3 py-1.5 text-xs text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                  重置
                </button>
                <button type="button" @click="quickExtend(30)" :disabled="actionLoading"
                  class="px-3 py-1.5 text-xs text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                  +1个月
                </button>
                <button type="button" @click="quickExtend(90)" :disabled="actionLoading"
                  class="px-3 py-1.5 text-xs text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                  +3个月
                </button>
                <button type="button" @click="quickExtend(365)" :disabled="actionLoading"
                  class="px-3 py-1.5 text-xs text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                  +1年
                </button>
              </div>
            </div>

            <p class="mt-2 text-xs text-gray-500 dark:text-gray-400">
              必须晚于当前时间和开始时间
            </p>
          </div>
        </div>

        <!-- 操作按钮 -->
        <div class="flex justify-end gap-3 mt-6">
          <button @click="closeEditEndDateDialog" :disabled="actionLoading"
            class="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
            取消
          </button>
          <button @click="handleUpdateEndDate" :disabled="actionLoading || !newEndDate"
            class="px-4 py-2 text-sm text-white bg-indigo-600 hover:bg-indigo-700 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
            <span v-if="actionLoading">保存中...</span>
            <span v-else>保存</span>
          </button>
        </div>
      </div>
    </div>

    <!-- 退款确认对话框 -->
    <div
      v-if="showRefundDialog"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 dark:bg-opacity-70"
      @click.self="closeRefundDialog"
    >
      <div class="mx-4 w-full max-w-2xl rounded-lg bg-white p-6 shadow-xl dark:bg-gray-800">
        <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100">退款 / 作废订单</h3>
        <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">
          用于处理用户线下退款申请。若关联租户佣金尚未结算，系统将同步把该笔佣金标记为已退款。
        </p>

        <div class="mt-5 rounded-lg bg-gray-50 p-4 text-sm dark:bg-gray-700/60">
          <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <p class="text-xs uppercase tracking-wide text-gray-400">套餐名称</p>
              <p class="mt-1 font-medium text-gray-900 dark:text-gray-100">{{ displayLocalizedText(refundingSubscription?.plan.name) }}</p>
            </div>
            <div>
              <p class="text-xs uppercase tracking-wide text-gray-400">订阅 ID</p>
              <p class="mt-1 font-medium text-gray-900 dark:text-gray-100">#{{ refundingSubscription?.id }}</p>
            </div>
            <div>
              <p class="text-xs uppercase tracking-wide text-gray-400">支付状态</p>
              <p class="mt-1 font-medium text-gray-900 dark:text-gray-100">{{ formatPaymentStatus(refundingSubscription?.paymentStatus) }}</p>
            </div>
            <div>
              <p class="text-xs uppercase tracking-wide text-gray-400">支付金额</p>
              <p class="mt-1 font-medium text-gray-900 dark:text-gray-100">{{ formatCurrency(refundingSubscription?.actualAmount) }}</p>
            </div>
            <div>
              <p class="text-xs uppercase tracking-wide text-gray-400">商户订单号</p>
              <p class="mt-1 font-medium text-gray-900 dark:text-gray-100">{{ refundingSubscription?.outTradeNo || '未记录' }}</p>
            </div>
            <div>
              <p class="text-xs uppercase tracking-wide text-gray-400">支付时间</p>
              <p class="mt-1 font-medium text-gray-900 dark:text-gray-100">{{ formatDate(refundingSubscription?.paymentTime || null) }}</p>
            </div>
          </div>
        </div>

        <div class="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200">
          若该订单对应的租户佣金已进入结算批次或已结算，当前版本会阻止退款并提示线下处理。
        </div>

        <div class="mt-5 space-y-4">
          <div>
            <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">退款原因</label>
            <input
              v-model="refundForm.reason"
              type="text"
              maxlength="200"
              placeholder="例如：用户联系租户申请退款"
              class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200"
            />
          </div>

          <div>
            <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">备注</label>
            <textarea
              v-model="refundForm.note"
              rows="3"
              maxlength="500"
              placeholder="可选，补充退款背景或线下沟通信息"
              class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200"
            />
          </div>
        </div>

        <div class="mt-6 flex justify-end gap-3">
          <button
            @click="closeRefundDialog"
            :disabled="actionLoading"
            class="rounded border border-gray-300 px-4 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-100 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            取消
          </button>
          <button
            @click="handleRefundSubscription"
            :disabled="actionLoading || !refundForm.reason.trim()"
            class="rounded bg-amber-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span v-if="actionLoading">处理中...</span>
            <span v-else>确认退款并作废订单</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import type { User, UserSubscription } from '@/types/user/user';
import type { Plan } from '@/types/membership/plan';
import { getUserSubscriptionsAPI, createUserSubscriptionAPI, cancelUserSubscriptionAPI, updateSubscriptionEndDateAPI, activateSubscriptionAPI, refundUserSubscriptionAPI } from '@/api/user';
import { getAllPlansAPI } from '@/api/membership/plan';
import { HttpStatus } from '@/types/axios/httpStatus';
import { displayLocalizedText } from '@/types/membership/localized';
import toast from '@/utils/toaster';

interface AdminManagedSubscription extends UserSubscription {
  paymentStatus?: string;
  subscriptionType?: string;
  outTradeNo?: string | null;
  callbackNo?: string | null;
  paymentMethod?: string | null;
  paymentTime?: string | null;
  totalAmount?: number | null;
  actualAmount?: number | null;
}

const props = defineProps<{
  user: User | null;
}>();

const emit = defineEmits<{
  (e: 'subscription-updated'): void;
}>();

const loading = ref(false);
const actionLoading = ref(false);
const allSubscriptions = ref<AdminManagedSubscription[]>([]);
const plans = ref<Plan[]>([]);
const selectedPricingId = ref('');

// 修改到期时间相关状态
const showEditEndDateDialog = ref(false);
const editingSubscription = ref<AdminManagedSubscription | null>(null);
const newEndDate = ref('');

// 退款相关状态
const showRefundDialog = ref(false);
const refundingSubscription = ref<AdminManagedSubscription | null>(null);
const refundForm = ref({
  reason: '',
  note: '',
});

/**
 * 活跃订阅列表（所有 ACTIVE 状态的订阅）
 */
const activeSubscriptions = computed(() => {
  return allSubscriptions.value.filter(sub => sub.status === 'ACTIVE');
});

/**
 * 当前活跃订阅（第一个 ACTIVE 订阅，用于兼容）
 */
const currentSubscription = computed(() => {
  return activeSubscriptions.value[0] || null;
});

/**
 * 最小到期时间（当前时间）
 */
const minEndDate = computed(() => {
  const now = new Date();
  // 格式化为 datetime-local 输入框需要的格式：YYYY-MM-DDTHH:mm
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
});

/**
 * 订阅历史（所有订阅）
 */
const subscriptionHistory = computed(() => {
  return allSubscriptions.value;
});

/**
 * 格式化金额。
 */
const formatCurrency = (amount: number | null | undefined): string => {
  if (amount == null) return '未记录';
  return `¥${(amount / 100).toFixed(2)}`;
};

/**
 * 格式化支付状态。
 */
const formatPaymentStatus = (paymentStatus?: string | null): string => {
  const paymentStatusMap: Record<string, string> = {
    UNPAID: '未支付',
    PAID: '已支付',
    REFUNDED: '已退款',
    FAILED: '支付失败',
  };
  return paymentStatus ? paymentStatusMap[paymentStatus] || paymentStatus : '未记录';
};

/**
 * 判断订阅是否显示退款入口。
 */
const canRefundSubscription = (subscription: AdminManagedSubscription): boolean => {
  return subscription.paymentStatus === 'PAID' && (subscription.actualAmount || 0) > 0;
};

/**
 * 格式化日期
 */
const formatDate = (date: string | null): string => {
  if (!date) return '未设置';
  return new Date(date).toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * 格式化订阅状态
 */
const formatSubscriptionStatus = (status: string): string => {
  const statusMap: Record<string, string> = {
    PENDING: '待激活',
    ACTIVE: '有效',
    EXPIRED: '已过期',
    CANCELLED: '已取消',
  };
  return statusMap[status] || status;
};

/**
 * 获取订阅状态徽章样式
 */
const getSubscriptionStatusClass = (status: string): string => {
  const baseClasses = 'inline-flex items-center rounded-md px-2 py-1 text-xs font-medium';
  switch (status) {
    case 'ACTIVE':
      return `${baseClasses} bg-green-100 text-green-700`;
    case 'PENDING':
      return `${baseClasses} bg-yellow-100 text-yellow-700`;
    case 'EXPIRED':
      return `${baseClasses} bg-red-100 text-red-700`;
    case 'CANCELLED':
      return `${baseClasses} bg-gray-100 text-gray-700`;
    default:
      return `${baseClasses} bg-gray-100 text-gray-700`;
  }
};

/**
 * 加载套餐列表
 */
const loadPlans = async () => {
  try {
    const response = await getAllPlansAPI();
    plans.value = response.data?.plans || [];
  } catch (error: any) {
    console.error('加载套餐列表失败:', error);
    toast.error('加载套餐列表失败');
  }
};

/**
 * 加载订阅信息
 */
const loadSubscription = async () => {
  if (!props.user) return;

  loading.value = true;
  try {
    // 调用 API 获取用户所有订阅
    const response = await getUserSubscriptionsAPI(props.user.id);
    allSubscriptions.value = response.data.subscriptions || [];
  } catch (error: any) {
    console.error('加载订阅信息失败:', error);
    toast.error('加载订阅信息失败');
  } finally {
    loading.value = false;
  }
};

/**
 * 创建订阅
 */
const handleCreateSubscription = async () => {
  if (!props.user || !selectedPricingId.value) return;

  actionLoading.value = true;
  try {
    const response = await createUserSubscriptionAPI(props.user.id, Number(selectedPricingId.value));

    // ✅ 只处理成功情况，错误由 axios 拦截器自动显示 toaster
    if (response.status === 200) {
      toast.success('订阅创建成功');
      selectedPricingId.value = '';
      await loadSubscription();
      emit('subscription-updated');
    }
    // ❌ 不需要 else，axios 拦截器已经显示了错误 toaster
  } finally {
    actionLoading.value = false;
  }
};

/**
 * 取消订阅
 */
const handleCancelSubscription = async (subscriptionId: number) => {
  if (!props.user) return;

  if (!confirm('确定要取消该订阅吗？')) return;

  actionLoading.value = true;
  try {
    await cancelUserSubscriptionAPI(props.user.id, subscriptionId);
    toast.success('订阅已取消');
    await loadSubscription();
    emit('subscription-updated');
  } catch (error: any) {
    console.error('取消订阅失败:', error);
    toast.error(error.message || '取消订阅失败');
  } finally {
    actionLoading.value = false;
  }
};

/**
 * 手动激活订阅
 *
 * 用于处理支付回调失败等特殊情况，管理员可以手动激活待支付（PENDING）状态的订阅
 */
const handleActivateSubscription = async (subscriptionId: number) => {
  if (!confirm('确定要手动激活该订阅吗？\n\n注意：此操作将直接激活订阅，无需支付信息。\n适用于支付回调失败等特殊情况。')) return;

  actionLoading.value = true;
  try {
    const response = await activateSubscriptionAPI(subscriptionId);

    // 检查响应状态
    if (response.status !== HttpStatus.HTTP_200_OK) {
      toast.error(response.message || '激活订阅失败');
      return;
    }

    toast.success('订阅已激活');
    await loadSubscription();
    emit('subscription-updated');
  } catch (error: any) {
    console.error('激活订阅失败:', error);
    toast.error(error.message || '激活订阅失败');
  } finally {
    actionLoading.value = false;
  }
};

/**
 * 打开修改到期时间对话框
 */
const handleEditEndDate = (subscription: UserSubscription) => {
  editingSubscription.value = subscription;
  // 将当前到期时间转换为 datetime-local 格式
  if (subscription.endDate) {
    const endDate = new Date(subscription.endDate);
    const year = endDate.getFullYear();
    const month = String(endDate.getMonth() + 1).padStart(2, '0');
    const day = String(endDate.getDate()).padStart(2, '0');
    const hours = String(endDate.getHours()).padStart(2, '0');
    const minutes = String(endDate.getMinutes()).padStart(2, '0');
    newEndDate.value = `${year}-${month}-${day}T${hours}:${minutes}`;
  }
  showEditEndDateDialog.value = true;
};

/**
 * 打开退款确认对话框。
 */
const openRefundDialog = (subscription: AdminManagedSubscription) => {
  refundingSubscription.value = subscription;
  refundForm.value = {
    reason: '',
    note: '',
  };
  showRefundDialog.value = true;
};

/**
 * 关闭退款确认对话框。
 */
const closeRefundDialog = () => {
  showRefundDialog.value = false;
  refundingSubscription.value = null;
  refundForm.value = {
    reason: '',
    note: '',
  };
};

/**
 * 退款并作废订单。
 */
const handleRefundSubscription = async () => {
  if (!props.user || !refundingSubscription.value) return;
  if (!refundForm.value.reason.trim()) {
    toast.error('请填写退款原因');
    return;
  }

  actionLoading.value = true;
  try {
    const response = await refundUserSubscriptionAPI(
      props.user.id,
      refundingSubscription.value.id,
      {
        reason: refundForm.value.reason.trim(),
        note: refundForm.value.note.trim() || undefined,
      },
    );

    if (response.status !== HttpStatus.HTTP_200_OK) {
      toast.error(response.message || '退款处理失败');
      return;
    }

    toast.success('退款处理成功');
    closeRefundDialog();
    await loadSubscription();
    emit('subscription-updated');
  } catch (error: any) {
    console.error('退款处理失败:', error);
    toast.error(error.message || '退款处理失败');
  } finally {
    actionLoading.value = false;
  }
};

/**
 * 关闭修改到期时间对话框
 */
const closeEditEndDateDialog = () => {
  showEditEndDateDialog.value = false;
  editingSubscription.value = null;
  newEndDate.value = '';
};

/**
 * 重置到原始到期时间
 */
const resetToOriginal = () => {
  if (!editingSubscription.value?.endDate) return;

  // 格式化为 datetime-local 格式
  const endDate = new Date(editingSubscription.value.endDate);
  const year = endDate.getFullYear();
  const month = String(endDate.getMonth() + 1).padStart(2, '0');
  const day = String(endDate.getDate()).padStart(2, '0');
  const hours = String(endDate.getHours()).padStart(2, '0');
  const minutes = String(endDate.getMinutes()).padStart(2, '0');

  newEndDate.value = `${year}-${month}-${day}T${hours}:${minutes}`;
};

/**
 * 快捷延长到期时间
 *
 * @param days - 延长的天数
 */
const quickExtend = (days: number) => {
  if (!editingSubscription.value?.endDate) return;

  // 计算新的到期时间（在当前到期时间基础上延长）
  const currentEndDate = new Date(editingSubscription.value.endDate);
  const newEndDateTime = new Date(currentEndDate.getTime() + days * 24 * 60 * 60 * 1000);

  // 格式化为 datetime-local 格式
  const year = newEndDateTime.getFullYear();
  const month = String(newEndDateTime.getMonth() + 1).padStart(2, '0');
  const day = String(newEndDateTime.getDate()).padStart(2, '0');
  const hours = String(newEndDateTime.getHours()).padStart(2, '0');
  const minutes = String(newEndDateTime.getMinutes()).padStart(2, '0');

  newEndDate.value = `${year}-${month}-${day}T${hours}:${minutes}`;
};

/**
 * 更新订阅到期时间
 */
const handleUpdateEndDate = async () => {
  if (!props.user || !editingSubscription.value || !newEndDate.value) return;

  actionLoading.value = true;
  try {
    // 将 datetime-local 格式转换为 ISO 8601 格式
    const newEndDateTime = new Date(newEndDate.value);
    const isoString = newEndDateTime.toISOString();

    const response = await updateSubscriptionEndDateAPI(
      props.user.id,
      editingSubscription.value.id,
      isoString
    );

    // 检查响应状态
    if (response.status !== HttpStatus.HTTP_200_OK) {
      toast.error(response.message || '修改订阅到期时间失败');
      return;
    }

    toast.success('订阅到期时间已更新');
    closeEditEndDateDialog();
    await loadSubscription();
    emit('subscription-updated');
  } catch (error: any) {
    console.error('修改订阅到期时间失败:', error);
    toast.error(error.message || '修改订阅到期时间失败');
  } finally {
    actionLoading.value = false;
  }
};

// 初始化加载
watch(
  () => props.user,
  (newUser) => {
    if (newUser) {
      loadPlans();
      loadSubscription();
    }
  },
  { immediate: true }
);
</script>
